import React from 'react';
import AppHeader from '../components/layout/AppHeader';

const sections = [
    {
        id: 'introduction',
        title: '1. Introduction',
        content: `TechUnity Global ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform at techunityglobal.com (the "Platform"). Please read this policy carefully. If you disagree with its terms, please discontinue use of the Platform.`,
    },
    {
        id: 'information-we-collect',
        title: '2. Information We Collect',
        content: `We collect information you provide directly, including:
• Account information: name, email address, username, password
• Profile data: bio, skills, location, professional title, availability, portfolio URLs
• Payment information: processed securely via our payment partners (we do not store raw card data)
• Communication data: messages sent within the platform, contact form submissions
• Usage data: pages visited, features used, time spent on the Platform
• Device data: IP address, browser type, operating system (for security and analytics)`,
    },
    {
        id: 'how-we-use',
        title: '3. How We Use Your Information',
        content: `We use collected information to:
• Provide, operate, and maintain the Platform
• Match talent with relevant projects via the Dispatch system
• Personalize your experience and recommendations
• Process payments and manage financial transactions
• Send account-related notifications and service updates
• Monitor for fraud, abuse, and security threats
• Improve our platform through aggregated analytics
• Respond to your support requests and inquiries

We do NOT sell your personal data to third parties.`,
    },
    {
        id: 'data-sharing',
        title: '4. Data Sharing & Disclosure',
        content: `We may share your information with:
• Other platform users: only the profile data you choose to make public (controlled via your privacy settings)
• Payment processors: Flutterwave, NowPayments, and other payment providers — only what is necessary to complete transactions
• Service providers: cloud infrastructure, email delivery, analytics — bound by strict data processing agreements
• Legal authorities: if required by law, court order, or to protect the rights, property, or safety of TechUnity Global or others

We will never share your email, phone number, or private data without your explicit consent.`,
    },
    {
        id: 'data-security',
        title: '5. Data Security',
        content: `We implement industry-standard security measures including:
• Encryption in transit (HTTPS/TLS) and at rest (AES-256)
• Hashed and salted password storage using bcrypt
• Multi-Factor Authentication (MFA) available for all accounts
• Rate limiting and brute-force protection on all authentication endpoints
• Regular security audits and vulnerability assessments
• Access controls: principle of least privilege applied to all internal systems

Despite these measures, no system is 100% secure. We encourage you to use a strong, unique password and enable MFA.`,
    },
    {
        id: 'cookies',
        title: '6. Cookies & Tracking',
        content: `We use cookies and similar technologies to:
• Maintain your authenticated session (essential cookies — cannot be disabled)
• Remember your preferences
• Analyse Platform usage through privacy-friendly analytics

You can control non-essential cookies through your browser settings. Blocking essential cookies will prevent you from logging in.`,
    },
    {
        id: 'your-rights',
        title: '7. Your Rights',
        content: `Depending on your jurisdiction, you may have the right to:
• Access the personal data we hold about you
• Request correction of inaccurate data
• Request deletion of your account and associated data ("right to be forgotten")
• Object to or restrict processing of your data
• Data portability — receive your data in a machine-readable format
• Withdraw consent at any time (where processing is based on consent)

To exercise any of these rights, email us at privacy@techunityglobal.com. We will respond within 30 days.`,
    },
    {
        id: 'data-retention',
        title: '8. Data Retention',
        content: `We retain your data for as long as your account is active or as needed to provide services. After account deletion:
• Profile and public data is removed within 30 days
• Financial records are retained for 7 years as required by law
• Anonymised analytics data may be retained indefinitely
• Backup copies may persist for up to 90 days before full deletion`,
    },
    {
        id: 'third-party',
        title: '9. Third-Party Services',
        content: `Our Platform integrates with third-party services including:
• Cloudinary (media storage)
• Resend (transactional email)
• Flutterwave & NowPayments (payments)
• Google reCAPTCHA (bot protection)
• Upstash Redis (caching)

Each third party has its own privacy policy. We encourage you to review them. We are not responsible for third-party data practices.`,
    },
    {
        id: 'children',
        title: '10. Children\'s Privacy',
        content: `The Platform is not directed to individuals under the age of 16. We do not knowingly collect personal data from children. If you believe a child has provided us personal information, please contact us at privacy@techunityglobal.com and we will delete it promptly.`,
    },
    {
        id: 'changes',
        title: '11. Changes to This Policy',
        content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by posting a prominent notice on the Platform. The "Last Updated" date at the top of this document will always reflect when the policy was last revised. Continued use of the Platform after changes constitutes acceptance of the updated policy.`,
    },
    {
        id: 'contact',
        title: '12. Contact Us',
        content: `For privacy-related questions, requests, or complaints, please contact our Privacy team at:
Email: privacy@techunityglobal.com
Support: support@techunityglobal.com
Support Portal: https://techunityglobal.com/#/contact`,
    },
];

const PrivacyPolicy: React.FC = () => {
    const lastUpdated = 'February 19, 2026';

    return (
        <div className="min-h-screen bg-premium-wood-dark text-white font-sans">
            <AppHeader title="Privacy Policy" />

            <main className="container mx-auto px-4 py-12 max-w-4xl">
                {/* Hero */}
                <div className="mb-12">
                    <div className="inline-flex items-center gap-2 bg-premium-gold/10 border border-premium-gold/30 rounded-full px-4 py-1.5 text-premium-gold text-sm font-medium mb-6">
                        🔒 Privacy Policy
                    </div>
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-premium-gold mb-4">
                        Privacy Policy
                    </h1>
                    <p className="text-white/60 text-sm">
                        Last Updated: <span className="text-premium-gold font-semibold">{lastUpdated}</span>
                    </p>
                    <p className="mt-4 text-white/80 text-lg leading-relaxed">
                        At TechUnity Global, we take your privacy seriously. This policy outlines how we collect,
                        use, and protect your personal information within our platform. We believe your data
                        belongs to you — we are simply its custodians.
                    </p>
                </div>

                {/* Table of Contents */}
                <nav className="bg-black/30 border border-premium-gold/20 rounded-xl p-6 mb-12">
                    <h2 className="text-premium-gold font-bold text-lg mb-4">Contents</h2>
                    <ol className="space-y-2">
                        {sections.map((s) => (
                            <li key={s.id}>
                                <a
                                    href={`#${s.id}`}
                                    className="text-white/70 hover:text-premium-gold transition-colors text-sm"
                                >
                                    {s.title}
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>

                {/* Sections */}
                <div className="space-y-10">
                    {sections.map((section) => (
                        <section
                            key={section.id}
                            id={section.id}
                            className="bg-black/20 border border-white/5 rounded-xl p-8 scroll-mt-24"
                        >
                            <h2 className="text-xl font-bold text-white mb-4">{section.title}</h2>
                            <p className="text-white/75 leading-relaxed whitespace-pre-line text-sm">
                                {section.content}
                            </p>
                        </section>
                    ))}
                </div>

                {/* Footer CTA */}
                <div className="mt-16 bg-gradient-to-r from-premium-gold/10 to-transparent border border-premium-gold/20 rounded-xl p-8 text-center">
                    <p className="text-white/80 mb-4">
                        Have questions about how we handle your data?
                    </p>
                    <a
                        href="/#/contact"
                        className="inline-flex items-center gap-2 bg-premium-gold text-premium-wood-dark font-bold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
                    >
                        Contact Our Privacy Team →
                    </a>
                </div>
            </main>
        </div>
    );
};

export default PrivacyPolicy;
